import type { CommunityCommentSummary } from "./apiContracts";
import { byId, setText } from "./domUtils";
import type { Root } from "./prototypeTypes";
import { getRuntime } from "./runtimeAccessor";

/** 首屏渲染的评论数，超出部分通过 "Show more" 按钮增量加载。 */
const COMMENT_INITIAL_PAGE = 10;

export async function loadIdeaComments(root: Root, ideaId: string, isCurrent: () => boolean = () => true): Promise<void> {
	const rt = getRuntime(root);
	if (!rt?.api?.listCommunityIdeaComments) {
		if (!isCurrent()) return;
		paintComments(root, []);
    return;
  }
	try {
		const response = await rt.api.listCommunityIdeaComments(ideaId);
		if (!isCurrent()) return;
    const comments = response.comments ?? [];
    paintComments(root, comments);
    const count = byId(root, "ideaDetailComments");
    const authoritativeCount = Number(count?.textContent ?? '');
    if (!Number.isFinite(authoritativeCount) || authoritativeCount < comments.length) {
      setText(root, "ideaDetailComments", String(comments.length));
    }
	} catch {
		if (!isCurrent()) return;
		paintCommentsMessage(root, "Could not load comments right now.");
  }
}

export function paintCommentsLoading(root: Root): void {
  paintCommentsMessage(root, "Loading comments...");
}

export function paintCommentsMessage(root: Root, message: string): void {
  const list = byId(root, "ideaCommentList");
  if (list) list.replaceChildren(buildCommentEmpty(message));
}

function paintComments(root: Root, comments: CommunityCommentSummary[]) {
  const list = byId(root, "ideaCommentList");
  if (!list) return;
  if (comments.length === 0) {
    list.replaceChildren(buildCommentEmpty("No comments yet. Start the discussion."));
    return;
  }
  // 首屏只渲染前 COMMENT_INITIAL_PAGE 条，超出部分通过 "Show more" 按钮追加。
  // 数据仍一次拉取（limit=200），但 DOM 分批渲染，避免长列表性能问题。
  renderCommentPage(list, comments, 0, COMMENT_INITIAL_PAGE);
}

/** 渲染 comments[start..end) 区间到 list，并在末尾追加 "Show more" 按钮（如有剩余）。 */
function renderCommentPage(list: HTMLElement, comments: CommunityCommentSummary[], start: number, end: number): void {
  const slice = comments.slice(start, end);
  const hasMore = end < comments.length;
  const frag = document.createDocumentFragment();
  for (const comment of slice) frag.appendChild(buildCommentItem(comment));
  if (hasMore) {
    const remaining = comments.length - end;
    frag.appendChild(buildShowMoreButton(comments, end));
    void remaining; // count shown in button label
  }
  // 首屏（start===0）替换全部；后续追加时移除旧 "Show more" 按钮再 append。
  if (start === 0) {
    list.replaceChildren(frag);
  } else {
    // 移除上一个 "Show more" 按钮（class=comment-show-more）
    list.querySelector(".comment-show-more")?.remove();
    list.appendChild(frag);
  }
}

function buildShowMoreButton(comments: CommunityCommentSummary[], nextStart: number): HTMLElement {
  const btn = document.createElement("button");
  btn.className = "comment-show-more";
  btn.type = "button";
  const remaining = comments.length - nextStart;
  const nextBatch = Math.min(COMMENT_INITIAL_PAGE, remaining);
  btn.textContent = `Show ${nextBatch} more ${remaining === 1 ? "comment" : "comments"} (${remaining} remaining)`;
  btn.addEventListener("click", () => {
    const list = btn.parentElement;
    if (!list) return;
    renderCommentPage(list as HTMLElement, comments, nextStart, nextStart + COMMENT_INITIAL_PAGE);
  });
  return btn;
}

function buildCommentEmpty(message: string): HTMLElement {
	const empty = document.createElement('div');
	empty.className = 'loading-placeholder empty-state-inline';
	const icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
	icon.setAttribute('aria-hidden', 'true');
	icon.setAttribute('viewBox', '0 0 24 24');
	icon.setAttribute('width', '28');
	icon.setAttribute('height', '28');
	icon.setAttribute('fill', 'none');
	icon.setAttribute('stroke', 'currentColor');
	icon.setAttribute('stroke-width', '1.5');
	icon.innerHTML = '<path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>';
	const label = document.createElement('span');
	label.textContent = message;
	empty.appendChild(icon);
	empty.appendChild(label);
	return empty;
}

function buildCommentItem(comment: CommunityCommentSummary): HTMLElement {
  const item = document.createElement("div");
  item.className = "comment-item";
  const header = document.createElement("div");
  header.className = "comment-header";
  const displayName = comment.owner ? "You" : comment.authorName?.trim() || "Community member";
  const avatar = document.createElement("div");
  avatar.className = "comment-avatar";
  avatar.textContent = displayName.charAt(0).toUpperCase();
  const author = document.createElement("span");
  author.className = "comment-author";
  author.textContent = displayName;
  const time = document.createElement("span");
  time.className = "comment-time";
  time.textContent = formatCommentTime(comment.createdAt);
  header.appendChild(avatar);
  header.appendChild(author);
  header.appendChild(time);
  const body = document.createElement("div");
  body.className = "comment-body";
  body.textContent = comment.body;
  item.appendChild(header);
  item.appendChild(body);
  return item;
}

function formatCommentTime(iso: string): string {
  const ts = Date.parse(iso);
  if (!Number.isFinite(ts)) return "Recently";
  const minutes = Math.max(0, Math.round((Date.now() - ts) / 60_000));
  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  return `${days}d ago`;
}
